import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

// Section Components
import HeroSection from '../components/sections/HeroSection';
import TrustStrip from '../components/sections/TrustStrip';
import ServicesSection from '../components/sections/ServicesSection';
import ProcessSection from '../components/sections/ProcessSection';
import TeamSection from '../components/sections/TeamSection';
import LeadSection from '../components/sections/LeadSection';

const DynamicHome = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPageLayout();
  }, []);

  const loadPageLayout = async () => {
    try {
      setLoading(true);
      // Fetch published layout
      const response = await apiClient.get('/api/page-layouts/home?mode=published', { skipAuth: true });
      
      if (response.sections && response.sections.length > 0) {
        // Sort by order_index and filter enabled sections
        const enabledSections = response.sections
          .filter(s => s.is_enabled)
          .sort((a, b) => a.order_index - b.order_index);
        
        setSections(enabledSections);
      } else {
        // Fallback to static sections if no layout is published
        setError('No published layout found - showing default');
      }
    } catch (error) {
      console.error('Failed to load page layout:', error);
      setError('Failed to load layout');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section) => {
    const props = section.props_json || {};
    
    switch (section.section_key) {
      case 'hero':
        return <HeroSection key={section._id} {...props} />;
      
      case 'trust':
        return <TrustStrip key={section._id} badges={props.badges} />;
      
      case 'services':
        return <ServicesSection key={section._id} title={props.title} items={props.items} />;
      
      case 'process':
        return <ProcessSection key={section._id} {...props} />;
      
      case 'team':
        return <TeamSection key={section._id} {...props} />;
      
      case 'cta':
      case 'lead':
        return <LeadSection key={section._id} {...props} />;
      
      case 'custom':
        return renderCustomSection(section);
      
      default:
        return null;
    }
  };

  const renderCustomSection = (section) => {
    const props = section.props_json || {};
    
    return (
      <section key={section._id} className="py-20 bg-primary-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {props.title && (
            <h2 className="text-4xl font-playfair font-bold text-center mb-12">
              {props.title}
            </h2>
          )}
          {props.content && (
            <div className="prose prose-invert max-w-none">
              <p className="text-neutral-300 text-center">{props.content}</p>
            </div>
          )}
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-accent-gold" />
      </div>
    );
  }

  // Fallback to static sections if error or no sections
  if (error || sections.length === 0) {
    return (
      <div className="min-h-screen">
        <HeroSection />
        <TrustStrip />
        <ServicesSection />
        <ProcessSection />
        <TeamSection />
        <LeadSection />
      </div>
    );
  }

  // Render dynamic sections
  return (
    <div className="min-h-screen">
      {sections.map(renderSection)}
    </div>
  );
};

export default DynamicHome;
